import { useMemo, useRef } from "react";
import {
  Home,
  GraduationCap,
  IdCard,
  Car,
  CheckCircle2,
  AlertCircle,
  Circle,
  Upload,
  Info,
  Check,
  Paperclip,
  FileText,
} from "lucide-react";
import PropTypes from "prop-types";

// Compara o conteúdo de dois arrays de schema por id+required+hint+label
// (evita recriar a cada render quando o pai passa um array literal).
function schemasEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.label !== y.label ||
      x.required !== y.required ||
      x.hint !== y.hint
    ) {
      return false;
    }
  }
  return true;
}

// Estabiliza a referência de `schema` comparando o conteúdo entre renders.
// Útil quando o componente pai passa um array literal inline.
function useStableSchema(schema) {
  const ref = useRef(schema);
  if (!schemasEqual(schema, ref.current)) {
    ref.current = schema;
  }
  return ref.current;
}

/**
 * DocumentUploadForm — controlado pelo TCA.
 *
 * Recebe os arquivos anexados (`filesById`) e o status de upload (`statusById`)
 * do componente pai, que já cuida do `handleAnexoChange` (envio ao backend).
 * Isso mantém a integração com `formData` intacta.
 *
 * `schema` define os campos:
 *   { id, label, required, hint?, icon? }
 *
 * `onAnexar(id, file)` é chamado quando o usuário escolhe um arquivo.
 *
 * `highlightIds` (opcional) — IDs vindos de
 * `response.data.anexos_obrigatorios` no 400 do backend. Faz os cards
 * entrarem em modo "destacado" (borda vermelha + tooltip).
 */
export default function DocumentUploadForm({
  schema,
  filesById = {},
  statusById = {},
  onAnexar,
  missingMessage,
  topNote,
  observations,
  insertObservations,
  onInsertObservationsChange,
  onObservationsChange,
  showObservationsSection = true,
  highlightIds = [],
  disabled = false,
}) {
  const icons = { Home, GraduationCap, IdCard, Car, FileText };

  // Renderiza "Observação: ..." só se houver pendência de documento obrigatório.
  // `useStableSchema` mantém a referência entre renders mesmo quando o pai
  // (TCA) passa o `schema` como array literal inline.
  const stableSchema = useStableSchema(schema);
  const missingRequired = useMemo(
    () => stableSchema.some((doc) => doc.required && !filesById[doc.id]),
    [stableSchema, filesById],
  );
  const highlightSet = useMemo(
    () => new Set(Array.isArray(highlightIds) ? highlightIds : []),
    [highlightIds],
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        width: "100%",
        padding: "0 1rem 1rem 1rem",
        boxSizing: "border-box",
      }}
    >
      {topNote && (
        <p
          style={{
            margin: "0.25rem 0 0",
            fontSize: 13,
            fontStyle: "italic",
            lineHeight: 1.5,
            color: "#fff",
            textAlign: "center",
          }}
        >
          {topNote}
        </p>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${schema.length}, minmax(0, 1fr))`,
          gap: 14,
          width: "100%",
        }}
      >
        {schema.map((doc) => (
          <DocumentCard
            key={doc.id}
            doc={doc}
            icon={icons[doc.icon] || Home}
            file={filesById[doc.id]}
            status={statusById[doc.id]}
            onFileSelected={(file) => onAnexar(doc.id, file)}
            highlight={highlightSet.has(doc.id)}
            disabled={disabled}
          />
        ))}
      </div>

      {missingRequired && missingMessage && (
        <div
          style={{
            background: "#fff7e6",
            borderLeft: "3px solid #f59e0b",
            borderRadius: 6,
            padding: "10px 14px",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            boxSizing: "border-box",
          }}
        >
          <Info
            size={16}
            color="#b45309"
            style={{ marginTop: 1, flexShrink: 0 }}
          />
          <p
            style={{
              fontSize: 13,
              color: "#7c2d12",
              margin: 0,
              lineHeight: 1.45,
            }}
          >
            {missingMessage}
          </p>
        </div>
      )}

      {showObservationsSection && (
        <ObservationsBlock
          observations={observations}
          insertObservations={insertObservations}
          onInsertObservationsChange={onInsertObservationsChange}
          onObservationsChange={onObservationsChange}
        />
      )}
    </div>
  );
}

function DocumentCard({ doc, icon: Icon, file, status, onFileSelected, highlight = false, disabled = false }) {
  const inputId = `upload-${doc.id}`;

  const isAttached = Boolean(file);
  const isOptional = doc.hint === "Opcional" && !isAttached;

  // Override de borda/cor quando o backend sinalizou (400 com
  // anexos_obrigatorios). Mantém o status interno (anexado/pendente)
  // mas destaca o card visualmente.
  const highlightStyle = highlight
    ? {
        border: "1.5px solid #b91c1c",
        boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.12)",
      }
    : {};

  const visualStatus =
    status === "error"
      ? "erro"
      : isAttached
        ? "anexado"
        : isOptional
          ? "opcional"
          : "pendente";

  const statusConfig = {
    anexado: {
      icon: <CheckCircle2 size={16} color="#0f6e56" />,
      label: file?.name || "Anexado",
      color: "#0f6e56",
      bg: "rgba(15, 110, 86, 0.08)",
      border: "rgba(15, 110, 86, 0.35)",
    },
    pendente: {
      icon: doc.required ? (
        <AlertCircle size={16} color="#b45309" />
      ) : (
        <Circle size={16} color="#888780" />
      ),
      label: "Pendente",
      color: "#7c2d12",
      bg: doc.required ? "rgba(180, 83, 9, 0.08)" : "transparent",
      border: doc.required
        ? "rgba(180, 83, 9, 0.35)"
        : "rgba(180, 180, 175, 0.6)",
    },
    opcional: {
      icon: <Circle size={16} color="#888780" />,
      label: "Opcional",
      color: "#57534e",
      bg: "transparent",
      border: "rgba(180, 180, 175, 0.6)",
    },
    erro: {
      icon: <AlertCircle size={16} color="#b91c1c" />,
      label: "Falha no envio",
      color: "#7f1d1d",
      bg: "rgba(220, 38, 38, 0.08)",
      border: "rgba(220, 38, 38, 0.4)",
    },
  }[visualStatus];

  const handleChange = (e) => {
    if (disabled) {
      e.target.value = "";
      return;
    }
    const newFile = e.target.files?.[0] ?? null;
    if (newFile) onFileSelected(newFile);
    e.target.value = "";
  };

  const showProgress = status === "uploading" && !isAttached;

  return (
    <div
      style={{
        position: "relative",
        background: "#fff",
        border: `1px solid ${statusConfig.border}`,
        borderRadius: 9,
        padding: "10px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minHeight: 124,
        cursor: "default",
        transition: "all 0.18s ease",
        boxSizing: "border-box",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        ...highlightStyle,
      }}
      title={highlight ? "Anexo pendente, conforme retorno do servidor." : undefined}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: statusConfig.bg,
          }}
        >
          <Icon size={15} color="#5f5e5a" />
        </div>
        {statusConfig.icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontWeight: 600,
            fontSize: 12.5,
            margin: 0,
            color: "#1c1917",
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {doc.label}
          {doc.required && !isAttached && (
            <span
              title="Campo obrigatório"
              style={{
                marginLeft: 6,
                display: "inline-block",
                padding: "1px 6px",
                borderRadius: 999,
                background: "rgba(180, 83, 9, 0.12)",
                color: "#b45309",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                lineHeight: 1.4,
                verticalAlign: "middle",
              }}
            >
              Obrigatório
            </span>
          )}
        </p>
        <p
          style={{
            fontSize: 10.5,
            margin: "2px 0 0",
            color: statusConfig.color,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 500,
          }}
          title={statusConfig.label}
        >
          {showProgress ? "Enviando…" : statusConfig.label}
        </p>
      </div>

      <label
        htmlFor={disabled ? undefined : inputId}
        aria-disabled={disabled || undefined}
        title={
          disabled
            ? "Você não tem permissão para anexar documentos neste formulário."
            : undefined
        }
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "8px 14px",
          borderRadius: 8,
          background: isAttached ? "#01336d" : "transparent",
          border: "1px solid #01336d",
          color: isAttached ? "#ffffff" : "#01336d",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.3,
          cursor: disabled ? "not-allowed" : "pointer",
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.5 : 1,
          transition:
            "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease",
        }}
      >
        {isAttached ? <Paperclip size={12} /> : <Upload size={12} />}
        {isAttached ? "Substituir" : "Anexar"}
      </label>

      <input
        id={inputId}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  );
}

function ObservationsBlock({
  observations,
  insertObservations,
  onInsertObservationsChange,
  onObservationsChange,
}) {
  return (
    <div
      style={{
        background: "rgba(30, 41, 59, 0.55)",
        borderRadius: 10,
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        border: "1px solid rgba(255, 255, 255, 0.06)",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 4,
            height: 16,
            borderRadius: 2,
            background: "#185fa5",
          }}
        />
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            margin: 0,
            color: "#fff",
            letterSpacing: 0.2,
          }}
        >
          Observações para ajuste na emissão do contrato caso
          necessário
        </p>
      </div>

      <textarea
        value={observations}
        onChange={(e) => onObservationsChange(e.target.value)}
        placeholder="Este espaço deve ser utilizado para observações, particularidades ou informações adicionais para a emissão do contrato."
        rows={3}
        style={{
          width: "100%",
          boxSizing: "border-box",
          borderRadius: 8,
          border: "1px solid #cbd5e1",
          padding: 10,
          fontSize: 13,
          fontFamily: "inherit",
          resize: "vertical",
          background: "#ffffff",
          color: "#0f172a",
          outline: "none",
          transition: "border-color 0.18s ease",
        }}
      />
    </div>
  );
}

function SegmentButton({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "0.65rem 1rem",
        borderRadius: 8,
        border: active
          ? "1px solid #01336d"
          : "1px solid rgba(255, 255, 255, 0.18)",
        background: active ? "#01336d" : "rgba(255, 255, 255, 0.04)",
        color: active ? "#ffffff" : "#e7e5e4",
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.3,
        cursor: "pointer",
        transition:
          "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease",
      }}
    >
      {active && <Check size={13} />}
      {label}
    </button>
  );
}

DocumentUploadForm.propTypes = {
  schema: PropTypes.array.isRequired,
  filesById: PropTypes.object,
  statusById: PropTypes.object,
  onAnexar: PropTypes.func.isRequired,
  missingMessage: PropTypes.string,
  observations: PropTypes.string,
  insertObservations: PropTypes.bool,
  onInsertObservationsChange: PropTypes.func.isRequired,
  onObservationsChange: PropTypes.func.isRequired,
  showObservationsSection: PropTypes.bool,
  highlightIds: PropTypes.array,
  disabled: PropTypes.bool,
};

DocumentCard.propTypes = {
  doc: PropTypes.object.isRequired,
  icon: PropTypes.elementType.isRequired,
  file: PropTypes.object,
  status: PropTypes.string,
  onFileSelected: PropTypes.func.isRequired,
  highlight: PropTypes.bool,
  disabled: PropTypes.bool,
};

ObservationsBlock.propTypes = {
  observations: PropTypes.string,
  insertObservations: PropTypes.bool,
  onInsertObservationsChange: PropTypes.func.isRequired,
  onObservationsChange: PropTypes.func.isRequired,
};

SegmentButton.propTypes = {
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};
